import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas'; 
import "../index.css"; 
import { db } from '../firebase';
import { 
    collection, 
    addDoc, 
    serverTimestamp, 
    query, 
    where, 
    getDocs, 
    // Ensure all necessary imports are kept
} from 'firebase/firestore';
import { jsPDF } from 'jspdf'; // Assuming this was used for the invoice
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';

// --- CLOUDINARY CONFIG ---
const CLOUDINARY_CLOUD_NAME = 'dift6go5s'; 
const CLOUDINARY_UPLOAD_PRESET = 'ml_default';

// --- END CLOUDINARY CONFIG ---


// --- CONSTANTS AND HELPERS ---
const SUPPLIERS = {
    'booking.com': 'https://www.booking.com/searchresults.html?ss={HOTEL_NAME}',
    'expedia': 'https://www.expedia.com/Hotel-Search?query={HOTEL_NAME}',
    'agoda': 'https://www.agoda.com/search?SearchString={HOTEL_NAME}',
    'hotels.com': 'https://www.hotels.com/search?q={HOTEL_NAME}',
    'airbnb': 'https://www.airbnb.com/s/{HOTEL_NAME}',
};

// --- HELPER FUNCTION: Generates a unique 6-character booking key ---
const generateBookingKey = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
};

// --- CLOUDINARY UPLOAD HELPER FUNCTION (for ID file) ---
const uploadToCloudinary = async (file) => {
    if (!file) return null;
    const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('public_id', `guest_id_${Date.now()}_${file.name.replace(/\s/g, '_').substring(0, 30)}`);

    try {
        const response = await fetch(url, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            try {
                const errorData = await response.json();
                console.error("Cloudinary Upload Error:", errorData);
                throw new Error(`Cloudinary upload failed: ${errorData.error.message || response.statusText}`);
            } catch (e) {
                 throw new Error(`Cloudinary upload failed with status ${response.status}. Check your preset and network.`);
            }
        }

        const data = await response.json();
        return data.secure_url; 
    } catch (e) {
        console.error("Cloudinary upload failed:", e);
        throw new Error(`Cloudinary upload failed with status. Check your preset and network.`);
    }
};

// --- CLOUDINARY UPLOAD HELPER FUNCTION (for Signature) ---
const uploadSignatureToCloudinary = async (dataUrl) => {
    if (!dataUrl || dataUrl === 'data:,') return null;

    try {
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        
        const formData = new FormData();
        formData.append('file', blob);
        formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

        const uploadResponse = await fetch(
            `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
            {
                method: 'POST',
                body: formData,
            }
        );

        if (!uploadResponse.ok) {
            throw new Error('Cloudinary signature upload failed.');
        }

        const data = await uploadResponse.json();
        return data.secure_url;

    } catch (error) {
        console.error('Error uploading signature:', error);
        return null; 
    }
};

// --- END CLOUDINARY UPLOAD HELPER FUNCTION ---





// --- MAIN GuestRegistrationForm COMPONENT ---
const GuestRegistrationForm = () => {
    const { t } = useTranslation();
    // --- GUEST INVOICE COMPONENT (Retained in full, with minor thirdParty add-on correction) ---
const GuestInvoice = React.forwardRef(({ data, hotelList }, ref) => {
    
    // Standard styles (for printing/display)
    const invoiceStyle = { fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', padding: '30px', maxWidth: '700px', margin: '20px auto', backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' };
    const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '3px solid #007bff', paddingBottom: '15px', marginBottom: '20px' };
    const detailRowStyle = { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #eee' };
    const summaryStyle = { borderTop: '3px solid #007bff', marginTop: '25px', paddingTop: '15px', fontWeight: 'bold' };
    const preferenceRowStyle = { display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px dotted #eee', fontSize: '0.9em' };
    
    const hotel = hotelList.find(h => h.id === data.hotelId) || {};
    const hotelName = hotel.name || "Unknown Hotel";
    const hotelAddress = hotel.address || "N/A";
    const invoiceReference = data.bookingKey || `PRE-${Date.now()}`;
    
    // Helper to format currency
    const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;

    // Calculate Add-on Costs (Staff-Managed Add-ons: Cost $10 each - this is for internal staff summary)
    const ADDON_RATES = {
        breakfast: 10.00, parking: 10.00, airportTransfer: 10.00, extraBed: 10.00, 
        roomUpgrade: 10.00, spaAppointment: 10.00
    };
    
    
    const selectedAddons = [];
    for (const [key, price] of Object.entries(ADDON_RATES)) {
        if (data[key]) {
            
            // Format key for display
            const displayName = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
            selectedAddons.push({ name: displayName, price: price });
        }
    }

    // Helper for displaying preferences consistently
    const renderPreferences = () => (
        <>
            <h4 style={{ color: '#007bff', marginTop: '20px', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>{t('registration.guest_preferences')}</h4>
            <div style={preferenceRowStyle}>
                <span>{t('registration.bed_type_pref')}</span>
                <span>**{data.bedTypePreference || t('registration.none')}**</span>
            </div>
            <div style={preferenceRowStyle}>
                <span>{t('registration.floor_pref')}</span>
                <span>**{data.floorPreference || t('registration.none')}**</span>
            </div>
          
            <div style={preferenceRowStyle}>
                <span>{t('registration.quiet_high_floor')}</span>
                <span>**{data.quietHighFloor || t('registration.no_preference')}**</span>
            </div>
            <div style={preferenceRowStyle}>
                <span>{t('registration.early_checkin')}</span>
                <span>**{data.earlyCheckInRequest ? t('registration.requested') : t('registration.no')}**</span>
            </div>
            <div style={preferenceRowStyle}>
                <span>{t('registration.late_checkout')}</span>
                <span>**{data.lateCheckOutRequest ? t('registration.requested') : t('registration.no')}**</span>
            </div>
            <div style={preferenceRowStyle}>
                <span>{t('registration.accessibility')}</span>
                <span>**{data.accessibilityNeeds ? t('registration.yes') : t('registration.no')}**</span>
            </div>
        </>
    );

    // --- Third-Party Summary View (If path is thirdParty) ---
    if (data.path === 'thirdParty') {
        
        return (
            <div style={invoiceStyle} ref={ref}> 
                <div style={headerStyle}>
                    <div>
                        <h2 style={{ margin: 0, color: '#007bff' }}>{t('registration.summary_title')}</h2>
                        <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>{t('registration.invoice_id')} **{data.bookingKey || 'N/A'}**</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <h3 style={{ margin: 0 }}>{hotelName}</h3>
                        <p style={{ margin: '2px 0 0 0', fontSize: '0.8em' }}>{t('registration.hotel_id')} {data.hotelId}</p>
                    </div>
                </div>

                <h4 style={{ color: '#007bff' }}>{t('registration.booking_details_submitted')}</h4>
                <p><strong>{t('registration.full_name')}</strong> {data.fullName}</p>
                <p><strong>{t('registration.arrival')}</strong> {new Date(data.arrivalTime).toLocaleDateString()} {t('registration.at')} {new Date(data.arrivalTime).toLocaleTimeString()}</p>

                {/* Render Preferences for Third-Party */}
                {renderPreferences()}

                {/* Display Add-ons selected by third-party guest - CORRECTED to remove price */}
                {selectedAddons.length > 0 && ( 
                    <div style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                        {/* CORRECTED: Removed price from header */}
                        <h4 style={{ color: '#28a745', margin: '0 0 10px 0' }}>{t('registration.addons_requested')}</h4>
                        {selectedAddons.map(addon => (
                            <p key={addon.name} style={{ margin: '5px 0', fontSize: '0.9em' }}>
                                {/* CORRECTED: Removed price from list item */}
                                - {addon.name}
                            </p>
                        ))}
                    </div>
                )}
                
                <p style={{ color: '#dc3545', fontWeight: 'bold', marginTop: '15px' }}>
                    {t('registration.third_party_note')}
                </p>

                {/* Signature Display */}
                <div style={{ marginTop: '40px', paddingTop: '15px', borderTop: '1px dashed #ccc', textAlign: 'center' }}>
                    {data.signature && <img src={data.signature} alt="Guest Signature" style={{ border: '1px solid #ccc', maxHeight: '80px', display: 'block', margin: '10px auto' }} />}
                    <p style={{ margin: 0, fontSize: '0.9em', color: '#555' }}>{t('registration.signature_confirmation')}</p>
                </div>
                
                {/* ID/Document Reminder */}
                <p style={{ marginTop: '20px', fontSize: '0.8em', color: '#6c757d' }}>{t('registration.id_reminder')}{' '} <a href={data.idProofUrl} target="_blank" rel="noopener noreferrer">{t('registration.view_id')}</a></p>
            </div>
        );
    }

    // --- Direct Booking & Full Invoice View (Unchanged logic) ---
    const checkInDate = data.arrivalTime ? new Date(data.arrivalTime) : new Date();
    const checkOutDate = data.departureTime ? new Date(data.departureTime) : new Date();
    const diffTime = Math.abs(checkOutDate - checkInDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1; 

    const baseRoomCharge = (data.roomPrice || 0) * diffDays;
    
    
    // In this full invoice, we assume add-on prices are also per night for simplicity, unless explicitly stated otherwise.
    // Recalculate add-ons for the full invoice (if they were per night)
    // 
let addOnsTotal = 0;
const invoiceAddons = []; // This array now focuses only on the service name for the guest/form.

// 1. BREAKFAST
if (data.breakfast) {
    // Keep the cost calculation internal for total invoice calculation
    const cost = 10 * diffDays;
    addOnsTotal += cost;
    // PUSH NAME WITHOUT PRICE OR COST DETAILS
    invoiceAddons.push({ name: t('registration.breakfast_buffet'), cost: 0 }); // Cost set to 0 for display purposes on guest side
}

// 2. PARKING
if (data.parking) {
    const cost = 10 * diffDays;
    addOnsTotal += cost;
    // PUSH NAME WITHOUT PRICE OR COST DETAILS
    invoiceAddons.push({ name: t('registration.parking_label'), cost: 0 });
}

// 3. AIRPORT TRANSFER
if (data.airportTransfer) {
    const cost = 10;
    addOnsTotal += cost;
    // PUSH NAME WITHOUT PRICE OR COST DETAILS
    invoiceAddons.push({ name: t('registration.airport_transfer_label'), cost: 0 });
}

// 4. EXTRA BED
if (data.extraBed) {
    const cost = 10;
    addOnsTotal += cost;
    // PUSH NAME WITHOUT PRICE OR COST DETAILS
    invoiceAddons.push({ name: t('registration.extra_bed_label'), cost: 0 });
}
if (data.spaAppointment) {
    const cost = 10; // Adjust the cost as needed
    addOnsTotal += cost;
    invoiceAddons.push({ name: t('registration.spa_label'), cost: 0 });
}
    // Other staff-managed add-ons are not included in this guest invoice total
    

    const subTotal = baseRoomCharge ;
    const finalTotal = baseRoomCharge ;

    return (
        <div style={invoiceStyle} ref={ref}>
            <div style={headerStyle}>
                <div>
                    <h2 style={{ margin: 0, color: '#007bff' }}>{t('registration.estimated_invoice')}</h2>
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>{t('registration.invoice_no')} **{invoiceReference}**</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <h3 style={{ margin: 0 }}>{hotelName}</h3>
                    <p style={{ margin: '2px 0 0 0', fontSize: '0.8em' }}>{hotelAddress}</p>
                </div>
            </div>

            {/* Guest and Stay Info */}
           <div style={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    flexWrap: 'wrap', // Added to prevent overlap on phone
    gap: '10px',      // Adds space when stacked
    marginBottom: '15px' 
}}>
    <div style={{ flex: '1 1 200px' }}> {/* Minimum width before wrapping */}
        <p style={{ margin: 0 }}><strong>{t('registration.guest_label')}</strong> {data.fullName}</p>
        <p style={{ margin: 0 }}>
            <strong>{t('registration.booking_type')}</strong> 
            <span style={data.path === 'direct' ? {color: '#007bff', fontWeight: 'bold'} : {}}>
                {data.path === 'direct' ? t('registration.direct_booking_val') : t('registration.unknown')}
            </span>
        </p>
        <p style={{ margin: 0 }}><strong>{t('registration.payment_label')}</strong> {data.paymentMethod === 'payAtHotel' ? t('registration.pay_at_hotel') : t('registration.paid_online')}</p>
    </div>
    <div style={{ textAlign: 'right', flex: '1 1 200px', minWidth: 'fit-content' }}>
        <p style={{ margin: 0 }}><strong>{t('registration.checkin_label')}</strong> {new Date(data.arrivalTime).toLocaleDateString()}</p>
        <p style={{ margin: 0 }}><strong>{t('registration.checkout_label')}</strong> {new Date(data.departureTime).toLocaleDateString()}</p>
        <p style={{ margin: 0 }}><strong>{t('registration.nights_label')}</strong> {diffDays}</p>
    </div>
</div>

            {/* Billing Breakdown */}
            <div style={{ border: '1px solid #ccc', borderRadius: '5px', padding: '15px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{t('registration.charges_summary')}</h4>
                
                <div style={detailRowStyle}>
                    <span>{t('registration.room_stay_charge')} ({data.roomType || 'N/A'} - {data.roomNumber || 'TBA'})</span>
                    <span>{formatCurrency(baseRoomCharge)}</span>
                </div>
                
                {invoiceAddons.map((addon, index) => (
                    <div key={index} style={detailRowStyle}>
                        <span>{addon.name}</span>
                        <span>{formatCurrency(addon.cost)}</span>
                    </div>
                ))}
                
                {/* Subtotal row */}
                <div style={detailRowStyle}>
                    <span style={{ fontWeight: 'bold' }}>{t('registration.subtotal')}</span>
                    <span style={{ fontWeight: 'bold' }}>{formatCurrency(subTotal)}</span>
                </div>
                
               
                
                {/* Total */}
                <div style={{...detailRowStyle, ...summaryStyle}}>
                    <span>{t('registration.total_estimated_bill')}</span>
                    <span>{formatCurrency(finalTotal)}</span>
                </div>
            </div>

            {/* Preferences Section */}
            {renderPreferences()}

            {/* Signature Display */}
            <div style={{ marginTop: '40px', paddingTop: '15px', borderTop: '1px dashed #ccc', textAlign: 'center' }}>
                {data.signature && <img src={data.signature} alt="Guest Signature" style={{ border: '1px solid #ccc', maxHeight: '80px', display: 'block', margin: '10px auto' }} />}
                <p style={{ margin: 0, fontSize: '0.9em', color: '#555' }}>{t('registration.signature_confirmation')}</p>
            </div>
            
            <p style={{ marginTop: '20px', fontSize: '0.8em', color: '#6c757d' }}>{t('registration.id_reminder')}{' '} <a href={data.idProofUrl} target="_blank" rel="noopener noreferrer">{t('registration.view_id')}</a></p>
        </div>
    );
});
// --- END GUEST INVOICE COMPONENT ---
    // Flow States
    const [flowStep, setFlowStep] = useState('start');
    const [guestPath, setGuestPath] = useState(''); 
    const [currentSection, setCurrentSection] = useState(1);

    // ⭐ ADDED: NEW STATE FOR HOTEL FILTERING (KEPT) ⭐
    const [countryFilter, setCountryFilter] = useState('');
    const [cityFilter, setCityFilter] = useState('');
    const [hotelNameFilter, setHotelNameFilter] = useState('');
    // ------------------------------------
    
    const [formData, setFormData] = useState({
        path: '', 
        hotelId: '',
        fullName: '',
        email: '',
        phone: '',
        nationality: '', 
        arrivalTime: '',
        departureTime: '',
        numGuests: 1,
        roomId: '',
        roomNumber: '', 
        roomType: '', 
        roomPrice: 0.00, 
        paymentMethod: 'payAtHotel', 
        policiesConfirmed: false,
        bedTypePreference: '',
        floorPreference: '',
        earlyCheckInRequest: false,
        lateCheckOutRequest: false,
        quietHighFloor: '', 
        accessibilityNeeds: false,
        breakfast: false,
        parking: false,
        airportTransfer: false,
        extraBed: false,
        roomUpgrade: false,
        spaAppointment: false,
    });
    
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [idFile, setIdFile] = useState(null);
    const [hotelList, setHotelList] = useState([]);
    const [availableRooms, setAvailableRooms] = useState([]); 
    const signatureRef = useRef(null);
    const invoiceRef = useRef(null);
    
    // --- Print/Download Handler (kept intact) ---
    const handlePrint = () => {
        const input = invoiceRef.current;
        if (!input) {
            console.error("Invoice component reference is missing.");
            return;
        }

        setLoading(true); // Optional: Shows "Processing request..." during generation

        // The timeout helps ensure React has finished rendering before the capture starts
        setTimeout(() => {
            // Options: scale 2 improves quality; useCORS is important for external images (like your Cloudinary logo)
            html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
                const imgData = canvas.toDataURL('image/jpeg', 1.0);
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();
                
                const imgProps = pdf.getImageProperties(imgData);
                const imgWidth = pdfWidth;
                const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
                
                let heightLeft = imgHeight;
                let position = 0;

                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pdfHeight;

                // Handle multi-page content by adding new pages if the content overflows A4
                while (heightLeft >= -10) { 
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pdfHeight;
                }
                
                // Save the PDF, triggering the download
                const filename = `${t('registration.invoice_filename')}_${formData.bookingKey || t('registration.checkin')}.pdf`;
                pdf.save(filename);
                setLoading(false);
            }).catch(error => {
                console.error("PDF generation failed:", error);
                alert(t('registration.unknown_error'));
                setLoading(false);
            });
        }, 100);
    };


    // Effect 1: Fetch Hotels on Load (kept intact)
    useEffect(() => {
        const fetchHotels = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'hotels'));
                const hotels = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    address: doc.data().address || 'N/A',
                    country: doc.data().country || 'N/A',
                    city: doc.data().city || 'N/A'
                }));
                setHotelList(hotels);
                
                setFormData(prev => {
                    if (hotels.length > 0 && !prev.hotelId) {
                        return { ...prev, hotelId: hotels[0].id };
                    }
                    return prev;
                });
                
            } catch (err) {
                console.error("Error fetching hotels:", err);
                setError(t('registration.error_fetching_hotels'));
            }
        };
        fetchHotels();
    }, []);
    
    // Effect 2: Fetch Available Rooms when Hotel changes (kept intact)
    useEffect(() => {
        const fetchAvailableRooms = async () => {
            if (guestPath !== 'direct' || !formData.hotelId) { 
                setAvailableRooms([]);
                return;
            }

            try {
                const roomsRef = collection(db, 'rooms');
                const q = query(roomsRef, 
                    where('hotelId', '==', formData.hotelId), 
                    where('status', '==', 'available') 
                );
                
                const querySnapshot = await getDocs(q);
                const rooms = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setAvailableRooms(rooms);
                 
                if (formData.hotelId && formData.roomId && !rooms.find(r => r.id === formData.roomId)) {
                    setFormData(prev => ({ ...prev, roomId: '', roomNumber: '', roomType: '', roomPrice: 0.00 }));
                }
            } catch (err) {
                console.error("Error fetching available rooms:", err);
                setAvailableRooms([]);
            }
        };
        fetchAvailableRooms();
    }, [formData.hotelId, formData.roomId, guestPath]);

    // ⭐ ADDED: NEW LOGIC FOR FILTERING HOTELS (KEPT) ⭐
    const filteredHotels = hotelList.filter(hotel => {
        // Safe check for null/undefined properties and convert to lower case for case-insensitive search
        const name = hotel.name?.toLowerCase() || '';
        const country = hotel.country?.toLowerCase() || '';
        const city = hotel.city?.toLowerCase() || '';
        
        const matchesCountry = countryFilter ? country.includes(countryFilter.toLowerCase()) : true;
        const matchesCity = cityFilter ? city.includes(cityFilter.toLowerCase()) : true;
        const matchesName = hotelNameFilter ? name.includes(hotelNameFilter.toLowerCase()) : true;
        
        return matchesCountry && matchesCity && matchesName;
    });
    // ---------------------------------------------
    
    // --- HANDLERS (kept intact) ---
    const handleChange = (e) => { 
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : (name === 'roomPrice' || name === 'numGuests' ? parseFloat(value) : value) 
        }));
    }; 
    
    const handleRoomSelect = (e) => { 
        const selectedRoomId = e.target.value; 
        const selectedRoom = availableRooms.find(room => room.id === selectedRoomId);
        if (selectedRoom) { 
            setFormData(prev => ({ 
                ...prev, 
                roomId: selectedRoom.id, 
                roomNumber: selectedRoom.roomNumber, 
                roomType: selectedRoom.type, 
                roomPrice: parseFloat(selectedRoom.price), 
            }));
        } else { 
            setFormData(prev => ({ ...prev, roomId: '', roomNumber: '', roomType: '', roomPrice: 0.00 })); 
        } 
    };
    
    const handleFileChange = (e) => { 
        setIdFile(e.target.files[0]); 
    }; 
    
    const handleStartSelection = (path) => { 
        if (path === 'booked') { 
            setGuestPath('thirdParty');
            setFormData(prev => ({ ...prev, path: 'thirdParty' })); 
            setFlowStep('form'); 
        } else if (path === 'needToBook') { 
            setFlowStep('selectBookingType'); 
        } 
    };
    
    const handleSupplierSelect = (supplierKey) => { 
        if (!formData.hotelId) { 
            setError(t('registration.error_hotel_first')); 
            return;
        } 
        if (supplierKey === 'directBooking') { 
            setGuestPath('direct'); 
            setFormData(prev => ({ ...prev, path: 'direct' })); 
            setFlowStep('form');
        } else { 
            const hotel = hotelList.find(h => h.id === formData.hotelId) || { name: 'hotel' };
            const url = SUPPLIERS[supplierKey].replace('{HOTEL_NAME}', encodeURIComponent(hotel.name)); 
            window.open(url, '_blank');        
            setError(`${t('registration.redirecting_to')} ${supplierKey}. ${t('registration.return_after_booking')}`);
            setFlowStep('start');
        } 
    }; 
    
    const handleNextSection = () => {
    setError(''); // Clear previous errors
    
    if (currentSection === 1) {
        // Define specific requirements with names for the error message
        const checks = [
            { field: formData.hotelId, label: t('registration.hotel') },
            { field: formData.fullName, label: t('registration.full_name') },
            { field: formData.arrivalTime, label: t('registration.arrival_time') },
            { field: formData.departureTime, label: t('registration.departure_time') }
        ];

        // Find the first empty field
        const missing = checks.find(c => !c.field);
        
        if (missing) {
            setError(`${t('registration.missing_field')}: ${missing.label}`);
            return;
        }

        if (guestPath === 'direct' && !formData.roomId) {
            setError(t('registration.error_select_room'));
            return;
        }
    }

    if (currentSection === 2 && !idFile) {
        setError(t('registration.error_upload_id'));
        return;
    }

    setCurrentSection(prev => prev + 1);
};
    
    const handlePrevSection = () => { 
        setError('');
        setCurrentSection(prev => prev - 1);
    }; 
    
    // --- SUBMIT HANDLER (kept intact) ---
    const handleSubmit = async (e) => { 
        e.preventDefault(); 
        setError(''); 
        setLoading(true);
        
        if (!signatureRef.current || signatureRef.current.isEmpty()) { 
            setError(t('registration.error_signature_required')); 
            setLoading(false); 
            return;
        } 
        
        try { 
            if (!idFile || !formData.hotelId) { 
                setError(t('registration.error_missing_info'));
                setLoading(false); 
                return; 
            } 
            if (guestPath === 'direct' && !formData.policiesConfirmed) { 
                setError(t('registration.error_policies_required')); 
                setLoading(false); 
                return;
            } 
            
            const signature = signatureRef.current.toDataURL('image/png'); 
            const idProofUrl = await uploadToCloudinary(idFile); 
            const bookingKey = generateBookingKey();
            
            let newGuestData = { 
                ...formData, 
                bookingKey: bookingKey, 
                signature: signature, 
                idProofUrl: idProofUrl, 
                status: 'PENDING_DIGITAL_CHECKIN', 
                submittedAt: serverTimestamp(),
            };
            
            if (newGuestData.path === 'thirdParty') { 
                newGuestData = { 
                    ...newGuestData, 
                    roomId: null, 
                    roomNumber: null, 
                    roomPrice: 0.00, 
                    paymentMethod: 'thirdParty', 
                    policiesConfirmed: false, 
                    roomType: 'Third-Party',
                };
            } 
            
            await addDoc(collection(db, 'guestRegistrations'), newGuestData);
            
            setFormData(newGuestData); 
            setIsSubmitted(true); 
            
        } catch (err) { 
            console.error("Submission Error:", err);
            setError(`Failed to submit check-in request. Error: ${err.message}`); 
        } finally { 
            setLoading(false); 
        } 
    };
    
    // --- RENDER FLOW SECTIONS (kept intact, with modifications to renderFormSection1) ---
    const renderStartSelection = () => ( 
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <h2 style={{ color: '#007bff' }}>{t('registration.welcome_checkin')}</h2>
            <p>{t('registration.select_status')}</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '30px' }}>
                <button type="button" onClick={() => handleStartSelection('booked')} style={{ padding: '15px 30px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1.1em', fontWeight: 'bold', boxShadow: '0 4px #1e7e34' }} > 
                    {t('registration.already_booked')} 
                </button>
                <button type="button" onClick={() => handleStartSelection('needToBook')} style={{ padding: '15px 30px', backgroundColor: '#ffc107', color: 'black', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1.1em', fontWeight: 'bold', boxShadow: '0 4px #e0a800' }} > 
                    {t('registration.need_to_book')} 
                </button>
            </div>
        </div>
    ); 

    const renderBookingTypeSelection = () => (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <h2 style={{ color: '#007bff' }}>{t('registration.how_to_book')}</h2>
        <p>{t('registration.supplier_desc')}</p>
        
        {/* Changed from inline style to className for grid support */}
        <div className="supplier-container">
            
            <button 
                type="button" 
                onClick={() => handleSupplierSelect('directBooking')} 
                className="direct-booking-btn"
            > 
                {t('registration.direct_booking')}
            </button>

            {Object.keys(SUPPLIERS).map(supplier => (
                <button 
                    key={supplier} 
                    type="button" 
                    onClick={() => handleSupplierSelect(supplier)} 
                    className="supplier-button"
                > 
                    {t('registration.book_via')} {supplier} 
                </button>
            ))}
        </div>

        <button 
            type="button" 
            onClick={() => setFlowStep('start')} 
            style={{ 
                marginTop: '20px', 
                padding: '10px 20px', 
                backgroundColor: '#f8f9fa', 
                color: '#333', 
                border: '1px solid #ccc', 
                borderRadius: '5px',
                width: 'auto' // Prevents "Back" button from being full-width
            }}
        >
            &larr; {t('registration.back')}
        </button>
    </div>
);
    // Function that renders the form content based on currentSection
    const renderForm = () => (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            
            {/* --- SECTION 1: Hotel and Guest Info --- */}
            {currentSection === 1 && (
                <section>
                    <h3>{t('registration.need_to_book')}</h3>

                    {/* ⭐ ADDED: NEW FILTER UI BLOCK (KEPT) ⭐ */}
                    <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f4f4f4' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '1.1em', color: '#007bff' }}>{t('registration.find_hotel')}</h4>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <input
                                type="text"
                                placeholder="Filter by Country"
                                value={countryFilter}
                                onChange={(e) => setCountryFilter(e.target.value)}
                                style={{ flex: 1, padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                            <input
                                type="text"
                                placeholder="Filter by City"
                                value={cityFilter}
                                onChange={(e) => setCityFilter(e.target.value)}
                                style={{ flex: 1, padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                        </div>
                        <input
                            type="text"
                            placeholder={t('registration.filter_hotel_placeholder')}
                            value={hotelNameFilter}
                            onChange={(e) => setHotelNameFilter(e.target.value)}
                            style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <p style={{ margin: '5px 0 0 0', fontSize: '0.9em', color: '#6c757d' }}>{t('registration.showing_hotels', { count: filteredHotels.length })}</p>
                    </div>
                    {/* ⭐ END NEW FILTER UI BLOCK ⭐ */}

                    <label>{t('registration.select_hotel_label')}</label>
                    <select
                        name="hotelId"
                        value={formData.hotelId}
                        onChange={handleChange}
                        required
                        disabled={hotelList.length === 0}
                        style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    >
                        <option value="">
                            {hotelList.length === 0 ? t('registration.loading_hotels') : t('registration.select_hotel_option')}
                        </option>
                        {/* The options now use the filtered list */}
                        {filteredHotels.map(hotel => (
                            <option key={hotel.id} value={hotel.id}>
                                {hotel.name} - {hotel.city}, {hotel.country}
                            </option>
                        ))}
                    </select>

                   <label className="required-label">{t('registration.full_name_label')}</label>
<input
    type="text"
    name="fullName"
    placeholder="Full Name"
    value={formData.fullName}
    onChange={handleChange}
    required
    /* Removed inline style so it uses the 16px font and border from index.css */
/>
                    
                    <label>{t('registration.arrival_time_label')}</label>
                    <input
                        type="datetime-local"
                        name="arrivalTime"
                        value={formData.arrivalTime}
                        onChange={handleChange}
                        required
                        style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />

                    <label>{t('registration.departure_time_label')}</label>
                    <input
                        type="datetime-local"
                        name="departureTime"
                        value={formData.departureTime}
                        onChange={handleChange}
                        required
                        style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    
                    <label>{t('registration.num_guests_label')}</label>
                    <input
                        type="number"
                        name="numGuests"
                        value={formData.numGuests}
                        onChange={handleChange}
                        required
                        min="1"
                        style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />

                    {/* Direct Booking specific fields */}
                    {guestPath === 'direct' && (
                        <>
                            <label>{t('registration.room_selection_label')}</label>
                            <select
                                name="roomId"
                                value={formData.roomId}
                                onChange={handleRoomSelect}
                                required
                                disabled={availableRooms.length === 0}
                                style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                            >
                                <option value="">
                                    {availableRooms.length === 0 ? t('registration.no_rooms_available') : t('registration.select_room_option')}
                                </option>
                                {availableRooms.map(room => (
                                    <option key={room.id} value={room.id}>
                                        {room.roomNumber} ({room.type}) - ${room.price.toFixed(2)}/{t('registration.night')}
                                    </option>
                                ))}
                            </select>

                            <p style={{ margin: '5px 0 15px 0', fontSize: '0.9em' }}>
                                {t('registration.selected_room_price')} <strong>${formData.roomPrice.toFixed(2)}</strong> {t('registration.night')}.
                            </p>
                        </>
                    )}
                </section>
            )}

            {/* --- SECTION 2: Contact, ID, and Preferences --- */}
            {currentSection === 2 && (
                <section>
                    <h3>{t('registration.contact_id_pref_title')}</h3>
                    
                    <label>{t('registration.email_label')}</label>
                    <input
                        type="email"
                        name="email"
                        placeholder="Optional"
                        value={formData.email}
                        onChange={handleChange}
                        style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />

                    <label>{t('registration.phone_label')}</label>
                    <input
                        type="tel"
                        name="phone"
                        placeholder={t('registration.optional')}
                        value={formData.phone}
                        onChange={handleChange}
                        style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    
                    <label>{t('registration.nationality_label')}</label>
                    <input
                        type="text"
                        name="nationality"
                        placeholder={t('registration.nationality_placeholder')}
                        value={formData.nationality}
                        onChange={handleChange}
                        required
                        style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    
                    <label>{t('registration.upload_id_label')}</label>
                    <input
                        type="file"
                        onChange={handleFileChange}
                        required
                        style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <small style={{ color: '#6c757d' }}>{t('registration.accepted_formats')}</small>

                    <h4 style={{ marginTop: '20px', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Preferences & Requests</h4>
                    
                    <label>{t('registration.bed_type_pref')}</label>
                    <select name="bedTypePreference" value={formData.bedTypePreference} onChange={handleChange} style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                        <option value="">{t('registration.no_preference')}</option>
                        <option value="King">{t('registration.king')}</option>
                        <option value="Queen">{t('registration.queen')}</option>
                        <option value="Twin">{t('registration.twin')}</option>
                    </select>

                    <label>{t('registration.floor_pref')}</label>
                    <select name="floorPreference" value={formData.floorPreference} onChange={handleChange} style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                        <option value="">{t('registration.floor_no_pref')}</option>
                        <option value="High">{t('registration.floor_high')}</option>
                        <option value="Low">{t('registration.floor_low')}</option>
                    </select>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="quietHighFloor" name="quietHighFloor" checked={formData.quietHighFloor} onChange={handleChange} />
                        <label htmlFor="quietHighFloor">{t('registration.quiet_room')}</label>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="earlyCheckInRequest" name="earlyCheckInRequest" checked={formData.earlyCheckInRequest} onChange={handleChange} />
                        <label htmlFor="earlyCheckInRequest">{t('registration.early_checkin')}</label>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="lateCheckOutRequest" name="lateCheckOutRequest" checked={formData.lateCheckOutRequest} onChange={handleChange} />
                        <label htmlFor="lateCheckOutRequest">{t('registration.late_checkout')}</label>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="accessibilityNeeds" name="accessibilityNeeds" checked={formData.accessibilityNeeds} onChange={handleChange} />
                        <label htmlFor="accessibilityNeeds">{t('registration.accessibility')}</label>
                    </div>
                </section>
            )}

            {/* --- SECTION 3: Add-ons, Policies, and Signature --- */}
            {currentSection === 3 && (
                <section>
                    <h3>{t('registration.section3_title')}</h3>

                    <h4 style={{ marginTop: '20px', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>{t('registration.addons_title')}</h4>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="breakfast" name="breakfast" checked={formData.breakfast} onChange={handleChange} />
                        <label htmlFor="breakfast">{t('registration.addon_breakfast')} </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="parking" name="parking" checked={formData.parking} onChange={handleChange} />
                        <label htmlFor="parking">{t('registration.addon_parking')} </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="airportTransfer" name="airportTransfer" checked={formData.airportTransfer} onChange={handleChange} />
                        <label htmlFor="airportTransfer">{t('registration.addon_airport')} </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" id="extraBed" name="extraBed" checked={formData.extraBed} onChange={handleChange} />
                        <label htmlFor="extraBed">{t('registration.addon_extrabed')} </label>
                    </div>
                    
                    {/* Direct Booking Policy Confirmation */}
                    {guestPath === 'direct' && (
                        <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #dc3545', backgroundColor: '#f8d7da33', borderRadius: '5px' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#dc3545' }}>{t('registration.policies_title')}</h4>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input type="checkbox" id="policiesConfirmed" name="policiesConfirmed" checked={formData.policiesConfirmed} onChange={handleChange} required />
                                <label htmlFor="policiesConfirmed">{t('registration.policies_label')}.</label>
                            </div>
                        </div>
                    )}

                    <h4 style={{ marginTop: '20px', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>{t('registration.payment_title')}</h4>
                    <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} disabled={guestPath === 'thirdParty'} style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                        <option value="payAtHotel">{t('registration.pay_at_hotel')}</option>
                        <option value="paidOnline">{t('registration.paid_online')}</option>
                        {guestPath === 'thirdParty' && <option value="thirdParty">{t('registration.paid_third_party')}</option>}
                    </select>

                    <label style={{ marginTop: '20px' }}>{t('registration.signature_label')}</label>
                    <div style={{ border: '1px solid #000', borderRadius: '5px', marginBottom: '5px' }}>
                        <SignatureCanvas 
                            ref={signatureRef} 
                            penColor='black'
                            canvasProps={{ width: 560, height: 150, className: 'sigCanvas' }}
                        />
                    </div>
                    <button 
                        type="button" 
                        onClick={() => signatureRef.current && signatureRef.current.clear()}
                        style={{ 
                            backgroundColor: '#dc3545', 
                            color: 'white', 
                            padding: '5px 10px', 
                            border: 'none', 
                            borderRadius: '5px', 
                            cursor: 'pointer', 
                            fontSize: '0.9em', 
                            width: 'fit-content'
                        }}
                    >
                        {t('registration.clear_signature')}
                    </button>
                </section>
            )}

            {/* Navigation Buttons (kept intact) */}
            <div style={{ display: 'flex', marginTop: '20px', justifyContent: 'space-between' }}>
                {currentSection > 1 && (
                    <button type="button" onClick={handlePrevSection} disabled={loading} style={{ flex: 1, marginRight: '10px', backgroundColor: '#6c757d', color: 'white', padding: '10px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                        {t('registration.prev')}
                    </button>
                )}
                {currentSection < 3 ?
                (
                    <button type="button" onClick={handleNextSection} disabled={loading} style={{ flex: 1, marginLeft: currentSection === 1 ? '0' : '10px', backgroundColor: '#007bff', color: 'white', padding: '10px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                        {t('registration.next')}
                    </button>
                ) : (
                    <button type="submit" disabled={loading} style={{ flex: 1, marginLeft: currentSection === 1 ? '0' : '10px', backgroundColor: '#28a745', color: 'white', padding: '10px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                        {loading ? t('registration.submitting') : t('registration.submit_btn')}
                    </button>
                )}
            </div>
        </form>
    );
    
    // RENDER FINAL INVOICE SCREEN
    const renderSuccessScreen = () => (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <h2 style={{ color: '#28a745' }}>{t('registration.success_title')}</h2>
            <p>{t('registration.success_msg1')}{' '} <strong style={{ color: '#dc3545', fontSize: '1.2em' }}>{formData.bookingKey}</strong></p>
            <p>{t('registration.success_msg2')}</p>
            
            <div style={{ marginTop: '30px' }}>
                <GuestInvoice data={formData} hotelList={hotelList} ref={invoiceRef} />
            </div>
            
            <button 
                onClick={handlePrint} 
                style={{ 
                    marginTop: '20px', 
                    padding: '10px 20px', 
                    backgroundColor: '#007bff', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '5px' 
                }}
            >
                {t('registration.print_summary')}
            </button>
        </div>
    );


    // --- MAIN RENDER ---
    let content;
    if (isSubmitted) {
        content = renderSuccessScreen();
    } else if (flowStep === 'start') {
        content = renderStartSelection();
    } else if (flowStep === 'selectBookingType') {
        content = renderBookingTypeSelection();
    } else if (flowStep === 'form') {
        content = renderForm();
    }

    return (
        <div style={{ maxWidth: '600px', margin: '50px auto', padding: '20px', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
            <h2 style={{ textAlign: 'center', color: '#007bff' }}>{t('registration.title')}</h2>
            
            {error && <p style={{ color: 'red', fontWeight: 'bold', border: '1px solid #f8d7da', padding: '10px', backgroundColor: '#f8d7da33' }}>{error}</p>}

            {!isSubmitted && flowStep === 'form' && (
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <span style={{ fontWeight: 'bold', color: currentSection === 1 ? '#007bff' : '#6c757d' }}>{t('registration.step1')}</span> &raquo;
                    <span style={{ fontWeight: 'bold', color: currentSection === 2 ? '#007bff' : '#6c757d' }}>{t('registration.step2')}</span> &raquo;
                    <span style={{ fontWeight: 'bold', color: currentSection === 3 ? '#007bff' : '#6c757d' }}>{t('registration.step3')}</span>
                </div>
            )}
            
            {loading && <p style={{ textAlign: 'center', color: '#007bff' }}>{t('registration.processing')}.</p>}
            
            {content}
            
        </div>
    );
}

export default GuestRegistrationForm;